import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Preferences } from '@capacitor/preferences';
import { PushNotifications } from '@capacitor/push-notifications';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Scissors, Search, Loader2, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ClientPortal = () => {
  const navigate = useNavigate();
  const [linkInput, setLinkInput] = useState("");
  const [loading, setLoading] = useState(true);
  const [searching, setSearching] = useState(false);

  useEffect(() => {
    const checkSavedBarber = async () => {
      try {
        const { value: savedSlug } = await Preferences.get({ key: 'favorite_barber_slug' });
        if (savedSlug) {
          navigate(`/reservar/${savedSlug}`, { replace: true });
        } else {
          setLoading(false);
        }
      } catch (error) {
        setLoading(false);
      }
    };
    checkSavedBarber();
  }, [navigate]);

  // 🔔 Pedir permiso de notificaciones y guardar el token FCM
  const requestPushPermission = async () => {
    try {
      const permission = await PushNotifications.requestPermissions();
      if (permission.receive === 'granted') {
        await PushNotifications.register();

        // Escuchar el token cuando Firebase lo entrega
        PushNotifications.addListener('registration', async (tokenData) => {
          await Preferences.set({ key: 'fcm_token', value: tokenData.value });
          console.log('✅ FCM Token guardado:', tokenData.value);
        });

        PushNotifications.addListener('registrationError', (err) => {
          console.error('❌ Error registrando push:', err);
        });
      }
    } catch (error) {
      // No bloquear el flujo si falla — no todos los dispositivos soportan push
      console.warn('Push notifications no disponibles en este dispositivo:', error);
    }
  };

  const handleConnect = async (e) => {
    e.preventDefault();
    if (!linkInput.trim()) return;

    setSearching(true);
    try {
      let slug = linkInput.trim().toLowerCase();
      if (slug.includes("/")) {
        const parts = slug.split("/");
        slug = parts[parts.length - 1];
      }

      const response = await axios.get(`${API}/shop/${slug}`);

      if (response.data) {
        await Preferences.set({ key: 'favorite_barber_slug', value: slug });

        // 🔔 Pedir permiso de notificaciones al conectarse con la barbería
        await requestPushPermission();

        toast.success(`¡Conectado con ${response.data.shop_name}!`);
        navigate(`/reservar/${slug}`);
      }
    } catch (error) {
      toast.error("No encontramos ninguna barbería con ese enlace. Verifica que esté bien escrito.");
    } finally {
      setSearching(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#09090b] flex items-center justify-center">
        <Loader2 className="w-10 h-10 text-amber-500 animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#09090b] text-white flex flex-col pt-[max(2rem,env(safe-area-inset-top))] px-6 pb-6">
      <div className="flex-1 flex flex-col items-center justify-center max-w-sm mx-auto w-full">

        <div className="w-20 h-20 bg-amber-500/10 rounded-2xl flex items-center justify-center mb-8 border border-amber-500/20 shadow-[0_0_30px_rgba(245,158,11,0.15)]">
          <Scissors className="w-10 h-10 text-amber-500" />
        </div>

        <h1 className="font-heading text-3xl font-black uppercase text-center mb-3">
          Encuentra a tu <span className="text-amber-500">Barbero</span>
        </h1>
        <p className="text-zinc-400 text-center text-sm mb-10">
          Pega el link de reserva que te envió tu peluquería para conectarte automáticamente.
        </p>

        <form onSubmit={handleConnect} className="w-full space-y-4">
          <div className="relative">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-zinc-500" />
            <Input
              value={linkInput}
              onChange={(e) => setLinkInput(e.target.value)}
              placeholder="Ej: boose.com/vip o solo 'vip'"
              className="w-full bg-zinc-900 border-zinc-800 text-white rounded-xl pl-12 h-14 focus:border-amber-500 transition-colors"
              required
            />
          </div>

          <Button
            type="submit"
            disabled={searching}
            className="w-full bg-amber-500 text-black font-heading font-black uppercase tracking-widest h-14 rounded-xl shadow-[0_0_20px_rgba(245,158,11,0.2)] hover:bg-amber-400 transition-all"
          >
            {searching ? <Loader2 className="w-6 h-6 animate-spin" /> : (
              <>Conectar <ArrowRight className="w-5 h-5 ml-2" /></>
            )}
          </Button>
        </form>

      </div>

      <p className="text-center text-zinc-600 text-xs mt-auto">
        No necesitas crear una cuenta para agendar tus citas.
      </p>
    </div>
  );
};

export default ClientPortal;